import {
  SET_SAAS_TENANTS,
  ISetSaasTenantsAction,
  ADD_SAAS_TENANT,
  IAddSaasTenantAction,
  UPDATE_SAAS_TENANT,
  IUpdateSaasTenantAction,
  DELETE_SAAS_TENANT,
  IDeleteSaasTenantAction,
  CLEAR_SAAS_TENANTS,
  IClearSaasTenantsAction,
  UPDATE_SAAS_TENANT_USAGE,
  IUpdateSaasTenantUsageAction,
  SET_SAAS_TENANT_SEARCH_RESULTS,
  ISetSaasTenantSearchResultsAction,
  SET_SAAS_TENANT_SEARCH_TOO_MANY_RESULTS,
  ISetSaasTenantSearchTooManyResultsAction,
  ISaasTenantsState,
  SET_SAAS_TENANT_CONFIRMED_BY_BACKEND,
  ISetSaasTenantConfirmedByBackendAction,
} from './types';
import initialState from '../store/initialState';

// Helper function to ensure numeric values are numbers
const ensureNumber = (value: string | number | undefined, parser: (str: string) => number = parseFloat): number => {
  if (typeof value === 'number') return value || 0;
  if (typeof value === 'string') return parser(value) || 0;
  return 0;
};

type HandledActions =
  | ISetSaasTenantsAction
  | IAddSaasTenantAction
  | ISetSaasTenantConfirmedByBackendAction
  | IUpdateSaasTenantAction
  | IDeleteSaasTenantAction
  | IClearSaasTenantsAction
  | IUpdateSaasTenantUsageAction
  | ISetSaasTenantSearchResultsAction
  | ISetSaasTenantSearchTooManyResultsAction;

export const saasTenantsReducer = (state: ISaasTenantsState = initialState.saasTenants, action: HandledActions): ISaasTenantsState => {
  switch (action.type) {
    // Set saasTenants data
    case SET_SAAS_TENANTS:
      return {
        ...state,
        saasTenants: action.payload.map((tenant) => ({
          ...tenant,
          usage: tenant.usage
            ? {
                ...tenant.usage,
                total_cost: ensureNumber(tenant.usage.total_cost),
                total_prompt_tokens: ensureNumber(tenant.usage.total_prompt_tokens, parseInt),
                total_completion_tokens: ensureNumber(tenant.usage.total_completion_tokens, parseInt),
                total_reasoning_tokens: ensureNumber(tenant.usage.total_reasoning_tokens, parseInt),
                usage_count: ensureNumber(tenant.usage.usage_count, parseInt),
                profiles_used: ensureNumber(tenant.usage.profiles_used, parseInt),
              }
            : undefined,
        })),
      };

    // Add a saasTenant
    case ADD_SAAS_TENANT:
      return {
        ...state,
        saasTenants: [action.payload, ...state.saasTenants],
      };

    case SET_SAAS_TENANT_CONFIRMED_BY_BACKEND:
      // Set record as no longer on route and move to start of array
      return {
        ...state,
        saasTenants: [
          ...state.saasTenants
            .filter((saasTenant) => saasTenant.saas_tenant_id === action.saas_tenant_id)
            .map((saasTenant) => ({ ...saasTenant, onroute: false, updated_at: action.updated_at })),
          ...state.saasTenants.filter((saasTenant) => saasTenant.saas_tenant_id !== action.saas_tenant_id),
        ],
      };

    // Update a saasTenant
    case UPDATE_SAAS_TENANT:
      return {
        ...state,
        saasTenants: state.saasTenants.map((saasTenant) => (saasTenant.saas_tenant_id === action.payload.saas_tenant_id ? action.payload : saasTenant)),
      };

    // Delete saasTenant
    case DELETE_SAAS_TENANT:
      return {
        ...state,
        saasTenants: state.saasTenants.filter((saasTenant) => saasTenant.saas_tenant_id !== action.payload.saas_tenant_id),
      };

    // Clear saasTenants
    case CLEAR_SAAS_TENANTS:
      return {
        ...state,
        saasTenants: [],
      };

    // Update usage data for a specific tenant
    case UPDATE_SAAS_TENANT_USAGE:
      return {
        ...state,
        saasTenants: state.saasTenants.map((tenant) => {
          if (tenant.saas_tenant_id === action.payload.saas_tenant_id && tenant.usage) {
            const currentUsage = tenant.usage;
            const newUsage = action.payload.usage;

            return {
              ...tenant,
              usage: {
                ...currentUsage,
                total_prompt_tokens: currentUsage.total_prompt_tokens + newUsage.prompt_tokens,
                total_completion_tokens: currentUsage.total_completion_tokens + newUsage.completion_tokens,
                total_reasoning_tokens: currentUsage.total_reasoning_tokens + newUsage.reasoning_tokens,
                usage_count: currentUsage.usage_count + 1,
                last_usage: new Date().toISOString(),
              },
            };
          }
          return tenant;
        }),
      };

    // Set search results for tenant name autocomplete
    case SET_SAAS_TENANT_SEARCH_RESULTS:
      const existingSearchResultIds = new Set(state.searchResults.map((r) => r.saas_tenant_id));
      const newSearchResults = action.payload.filter((r) => !existingSearchResultIds.has(r.saas_tenant_id));
      return {
        ...state,
        searchResults: [...newSearchResults, ...state.searchResults],
        searchTooManyResults: false,
      };

    case SET_SAAS_TENANT_SEARCH_TOO_MANY_RESULTS:
      return {
        ...state,
        searchTooManyResults: action.payload,
      };

    default:
      return state;
  }
};
